
import _Quill from 'quill';
import { computed, inject, nextTick, ref, watch, onBeforeUnmount, onMounted } from 'vue';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import { GetAmpStorage, GetAvailableAccountKeys } from '@/constants/getters';
import { onClickOutside } from '@/directives/onClickOutside';

import QuillEditor from '@/components/rte/QuillEditor';

const Quill = window.Quill || _Quill;

export default {
  components: {
    QuillEditor,
  },

  directives: {
    'click-outside': onClickOutside,
  },

  props: {
    onChange: { type: Function, default: () => {} },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: true },
    text: { type: String, default: '' },
  },

  setup(props, { emit }) {
    const { store } = useStoreInNodes();

    const getGraph = inject('getGraph', () => {});
    const getNode = inject('getNode', () => {});

    const richText = ref(props.text);
    const editorContainer = ref(null);
    const quillEditor = ref(null);
    const quillInstance = ref(null);
    const hideEditor = ref(true);
    const clickedRange = ref(null);
    const placeholderText = computed(() => `<p class="rte-text__placeholder">${props.placeholder}</p>`);
    const previewText = computed(() => getQuillHTML(props.text));

    const accountKeys = computed(() => store.value.getters[`crmStore/${GetAvailableAccountKeys}`]);
    const crmValues = computed(() =>
      accountKeys.value.map((key) => ({ id: key.name, value: key.name, isCrmKey: true }))
    );

    const storage = computed(() => store.value.getters[`ampStore/${GetAmpStorage}`]);
    const storageValues = computed(() => Object.keys(storage.value).map((id) => ({ id, value: storage.value[id] })));

    // Used to get Quill markup so we can style the non-edit node in Quill fashion
    const getQuillHTML = (value) => {
      const content = value.replace(/<p>(&nbsp;|\s)<\/p>/gm, '<p></p>');
      const tmpDiv = document.createElement('div');
      const tmpQuill = new Quill(tmpDiv);
      const delta = tmpQuill.clipboard.convert({ html: content });
      tmpQuill.setContents(delta);
      const html = tmpDiv.getElementsByClassName('ql-editor')[0]?.innerHTML || value;
      tmpDiv.remove();
      return html;
    };

    watch(hideEditor, (val) => {
      if (!val) {
        onClickOutside.bind(editorContainer.value, { name: 'click-outside', value: onEditorBlur });
      } else {
        onClickOutside.unbind(editorContainer.value);
      }
    });

    onMounted(() => window.addEventListener('mention-clicked', onMentionClick));

    onBeforeUnmount(() => window.removeEventListener('mention-clicked', onMentionClick));

    const onEditorReady = (quill) => {
      quillInstance.value = quill;
      const editorFields = quillEditor.value.$el.getElementsByClassName('ql-editor');

      setupStorageToolbar();
      quillEditor.value?.pasteHTML(props.text, true, true);

      if (editorFields.length > 0) {
        editorFields[0].addEventListener('click', () => {
          quillInstance.value.focus();
          emit('click');
        });
      }

      nextTick(() => {
        // Bring the node to the front in case it's large and overlaps with the nodes below
        getGraph()?.getCellById(getNode()?.id)?.toFront();
        // move the cursor at the clicked position in the text or the end of the text
        quillInstance.value.setSelection(clickedRange.value || quillInstance.value.getLength(), 0);
      });
    };

    const getInitialNode = (node) => {
      const mentionParent = node.parentElement?.closest('.mention');
      return mentionParent || node;
    };

    const getTextNodeCharIndexInParent = (node) => {
      let charIndex = 0;
      let prevNode = node.previousSibling;
      while (prevNode) {
        if (prevNode.classList?.contains('mention')) {
          // Mentions only count as 1 in the Quill delta
          charIndex += 1;
        } else if (prevNode.outerHTML?.match(/<p><br><\/p>/)) {
          // Empty
          charIndex += 2;
        } else {
          let charCount = prevNode.data?.length || prevNode.innerText?.length || 0;
          // Remove text length of mentions as they're only counted as 1 char in the Quill delta
          const mentions = prevNode.getElementsByClassName?.('mention') || [];
          if (mentions.length) {
            Array.from(mentions).forEach((elem) => {
              // Keeping 1 char count for each mention as mentions are counted as 1 in the Quill delta
              charCount -= elem.innerText.length - 1;
            });
          }
          charIndex += charCount;
        }
        prevNode = prevNode.previousSibling;
      }
      return charIndex;
    };

    const getTextNodeCharIndex = (node, parent) => {
      let charIndex = node.outerHTML?.match(/<p><br><\/p>/) ? 1 : 0;
      let parentNode = getInitialNode(node);

      while (parentNode !== parent) {
        charIndex += getTextNodeCharIndexInParent(parentNode);
        parentNode = parentNode.parentNode;
      }

      return charIndex || 0;
    };

    const onTextClick = (event) => {
      if (props.disabled) {
        return;
      }

      const sel = window.getSelection();
      if (sel.rangeCount) {
        const range = sel.getRangeAt(0);
        const nodeIndex = getTextNodeCharIndex(sel.focusNode, event.currentTarget);
        clickedRange.value = nodeIndex + range.startOffset;

        if (event.target.matches('li[data-list]')) {
          const childIndex = Array.from(event.target.parentElement.children).indexOf(event.target);
          clickedRange.value += childIndex;
        }
      }
    };

    const onEditorClick = () => {
      if (props.disabled) {
        return;
      }

      hideEditor.value = false;
      quillInstance.value?.focus();
    };

    const onEditorBlur = () => {
      hideEditor.value = true;
      quillInstance.value.blur();
      clickedRange.value = null;
    };

    const setupStorageToolbar = () => {
      const toolbar = quillInstance.value.getModule('toolbar');
      toolbar.addHandler('storage', onStorageClick);
    };

    const onStorageClick = () => {
      const mention = quillInstance.value.getModule('mention');
      mention.openMenu('{{');
    };

    const onMentionClick = ({ event }) => {
      const { srcElement } = event;
      const parent = srcElement.closest('.rte-container');

      if (parent === editorContainer.value) {
        const range = quillInstance.value.getSelection();

        // Clicking on a mention blot always sets the cursor before the mention blot,
        // but by moving it 1 index, we position the cursor after the blot
        quillInstance.value.setSelection(range.index + 1, 0);

        /**
         * @TODO Open storage list when clicking on the mention blot
         * @see https://github.com/quill-mention/quill-mention/issues/109
         */
      }
    };

    const getStorageValues = (searchTerm, renderList, _mentionChar) => {
      const values = [...storageValues.value, ...crmValues.value];

      if (searchTerm.length === 0) {
        renderList(values, searchTerm);
      } else {
        const matches = [];
        for (let i = 0; i < values.length; i++) {
          if (~values[i].value.toLowerCase().indexOf(searchTerm.trim().toLowerCase())) {
            matches.push(values[i]);
          }
        }

        renderList(matches, searchTerm);
      }
    };

    const getMentionItem = (item) => {
      if (item.isCrmKey) {
        const crmItem = document.createElement('div');
        crmItem.classList.add('crm-wrapper');
        crmItem.innerHTML = `<div class="crm-key">${item.value}</div><div class="crm-tag">CRM</div>`;
        return crmItem;
      }

      return item.value;
    };

    const quillConfig = ref({
      theme: 'snow',
      placeholder: props.placeholder,
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ script: 'sub' }, { script: 'super' }],
          [{ header: 1 }, { header: 2 }, { list: 'ordered' }, { list: 'bullet' }],
          ['storage'],
          ['clean'],
        ],
        mention: {
          allowedChars: /^[A-Za-z\sÅÄÖåäö0-9_]*$/,
          mentionDenotationChars: ['{{'],
          renderLoading: () => 'Loading...',
          renderItem: getMentionItem,
          source: getStorageValues,
          onSelect: (item, insertItem) => {
            item.value = `${item.value}}}`;
            insertItem(item);
          },
        },
      },
    });

    return {
      richText,
      placeholderText,
      previewText,
      editorContainer,
      hideEditor,
      quillEditor,
      quillConfig,
      onEditorBlur,
      onEditorClick,
      onEditorReady,
      onTextClick,
    };
  },
};
